/**
 * 管理端课时账本查询 API（ADM-24，entitlement-ledger-queries，6-3）。
 *
 * 全部只读：全机构流水（默认全量倒序 + 学生/账户/类型筛选）、流水详情
 * （含两池前后值/幂等键/冲正原因）、学生账户摘要、账户对账单（守恒标记）。
 * 不提供任何修改余额或删除流水的入口（事实型数据只追加，更正经 ADM-23 冲正）。
 */
import { ApiPaths } from './api.config';
import type { Paginated } from './campuses.api';
import type { PeriodPassView } from './entitlement.api';
import { http } from './transport';

/** 课时类别（单账户双池；O-14 起 PERIOD=期限卡事件行，池变动恒 0 不入守恒）。 */
export type LessonKind = 'FORMAL' | 'GIFTED' | 'PERIOD';
export type LedgerEntryType =
  | 'GIFT'
  | 'PURCHASE'
  | 'DEDUCT'
  | 'EXPIRE'
  | 'FREEZE'
  | 'UNFREEZE'
  | 'REVERSAL'
  | 'ACTIVATE'
  | 'ADJUST'
  | 'USAGE';
export type LedgerAccountStatus = 'NORMAL' | 'EXPIRED_VOID' | 'ZERO';
export type LedgerBusinessRefType =
  | 'ENROLLMENT'
  | 'ORDER'
  | 'DEDUCTION'
  | 'REVERSAL'
  | 'MANUAL_GRANT'
  | 'PERIOD_PASS';

/** 账本流水列表行（含两池全快照——学生详情账本 Tab 列表直读「前 → 后」）。 */
export interface LedgerEntryListItem {
  id: string;
  createdAt: string;
  studentId: string;
  studentName: string;
  studentNo: string;
  accountId: string;
  skuName: string;
  lessonKind: LessonKind;
  type: LedgerEntryType;
  availableChange: number;
  availableAfter: number;
  /** 两池全快照：本笔前后正式课池可用（非所属池 before==after）。 */
  formalBefore: number;
  formalAfter: number;
  /** 两池全快照：本笔前后赠送课池可用（非所属池 before==after）。 */
  giftedBefore: number;
  giftedAfter: number;
  businessRefType: LedgerBusinessRefType;
  businessRefId: string;
  operatorName: string | null;
  /** O-14 additive：PERIOD 行回指的期限卡凭证 ID */
  periodPassId?: string | null;
  /** O-14 additive：PERIOD 行期限天数快照 */
  periodDays?: number | null;
}

/** 账本流水详情（两池全快照自解释，含冲正原因）。 */
export interface LedgerEntryDetail extends LedgerEntryListItem {
  accountStatus: LedgerAccountStatus;
  frozenChange: number;
  /** 两池全快照：本笔前后正式课池可用（非所属池 before==after）。 */
  formalBefore: number;
  formalAfter: number;
  /** 两池全快照：本笔前后赠送课池可用（非所属池 before==after）。 */
  giftedBefore: number;
  giftedAfter: number;
  /** 所属池冻结前后（一期恒 0，仅预留）。 */
  frozenBefore: number;
  frozenAfter: number;
  idempotencyKey: string | null;
  reason: string | null;
  operatorId: string | null;
}

/** 学生账户摘要项（同一学生同一 SKU 单行，四池呈现）。 */
export interface StudentAccountSummary {
  accountId: string;
  skuId: string;
  skuName: string;
  formalAvailable: number;
  formalFrozen: number;
  giftedAvailable: number;
  giftedFrozen: number;
  /**
   * 已购痕迹：曾有 PURCHASE 入账（余额耗尽仍 true，生命周期展示用）；
   * O-14 起口径扩为含期限卡开通行（lessonKind=PERIOD 的 PURCHASE 行）。
   */
  formalPurchased: boolean;
  status: LedgerAccountStatus;
  /** O-14 additive：该账户名下期限卡摘要（与 /students/:id/period-passes 同形，按购买先后）。 */
  periodPasses?: PeriodPassView[];
}

/** 账户对账单流水项（两池全快照，单行自解释）。 */
export interface StatementEntry {
  id: string;
  lessonKind: LessonKind;
  type: LedgerEntryType;
  availableChange: number;
  frozenChange: number;
  /** 两池全快照：本笔前后正式课池可用（非所属池 before==after）。 */
  formalBefore: number;
  formalAfter: number;
  /** 两池全快照：本笔前后赠送课池可用（非所属池 before==after）。 */
  giftedBefore: number;
  giftedAfter: number;
  /** 变动后所属池可用（服务端派生，恒等于所属池 after 快照）。 */
  availableAfter: number;
  /** 所属池冻结前后（一期恒 0，仅预留）。 */
  frozenBefore: number;
  frozenAfter: number;
  businessSource: string;
  businessRefType: LedgerBusinessRefType;
  businessRefId: string;
  idempotencyKey: string | null;
  reason: string | null;
  createdAt: string;
}

/** 账户对账单（getStatement 接线，单行双池）。 */
export interface EntitlementStatement {
  accountId: string | null;
  studentId: string;
  skuId: string;
  formalAvailable: number;
  formalFrozen: number;
  giftedAvailable: number;
  giftedFrozen: number;
  status: LedgerAccountStatus | null;
  consistent: boolean;
  entries: StatementEntry[];
  page: number;
  pageSize: number;
  total: number;
}

/** 流水列表查询参数。 */
export interface LedgerEntryListQuery {
  studentId?: string;
  accountId?: string;
  type?: LedgerEntryType;
  page?: number;
  pageSize?: number;
}

function buildQuery(base: string, query: object): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && `${value}` !== '') {
      params.set(key, `${value}`);
    }
  }
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

async function listEntries(
  query: LedgerEntryListQuery = {},
): Promise<Paginated<LedgerEntryListItem>> {
  return http.get<Paginated<LedgerEntryListItem>>(
    buildQuery(ApiPaths.entitlementLedger.entries, query),
  );
}

async function getEntry(entryId: string): Promise<LedgerEntryDetail> {
  return http.get<LedgerEntryDetail>(
    ApiPaths.entitlementLedger.entryDetail(entryId),
  );
}

async function getStudentAccounts(
  studentId: string,
): Promise<StudentAccountSummary[]> {
  const res = await http.get<{ items: StudentAccountSummary[] }>(
    ApiPaths.entitlementLedger.studentAccounts(studentId),
  );
  return res.items ?? [];
}

async function getStatement(
  accountId: string,
  page = 1,
  pageSize = 20,
): Promise<EntitlementStatement> {
  return http.get<EntitlementStatement>(
    buildQuery(ApiPaths.entitlementLedger.statement(accountId), {
      page,
      pageSize,
    }),
  );
}

/** 课时账本查询 API 唯一对外入口。 */
export const entitlementLedgerApi = {
  listEntries,
  getEntry,
  getStudentAccounts,
  getStatement,
};
